import { useEffect, useState } from 'react';
import { IconUpload } from '../../lib/icons.tsx';

interface Props {
  onFiles: (entries: Array<{ file: File; relativePath: string }>) => void;
  disabled?: boolean;
}

interface FsEntry {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  file: (cb: (f: File) => void, err: (e: unknown) => void) => void;
  createReader: () => { readEntries: (cb: (e: FsEntry[]) => void, err: (x: unknown) => void) => void };
}

/** 디렉토리 드롭을 재귀적으로 펼친다 (webkitGetAsEntry). */
async function walk(entry: FsEntry, prefix: string): Promise<Array<{ file: File; relativePath: string }>> {
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) => { entry.file(resolve, reject); });
    return [{ file, relativePath: prefix ? `${prefix}/${entry.name}` : entry.name }];
  }
  if (!entry.isDirectory) return [];

  const reader = entry.createReader();
  const children: FsEntry[] = [];
  // readEntries 는 한 번에 최대 100개만 준다 — 빈 배열이 올 때까지 반복해야 한다
  for (;;) {
    const batch = await new Promise<FsEntry[]>((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    if (batch.length === 0) break;
    children.push(...batch);
  }

  const nested = prefix ? `${prefix}/${entry.name}` : entry.name;
  const out: Array<{ file: File; relativePath: string }> = [];
  for (const child of children) out.push(...await walk(child, nested));
  return out;
}

export function DropZone({ onFiles, disabled }: Props) {
  const [active, setActive] = useState(false);

  useEffect(() => {
    let depth = 0;

    const onDragEnter = (e: DragEvent): void => {
      if (disabled || !e.dataTransfer?.types.includes('Files')) return;
      depth++;
      setActive(true);
    };
    const onDragOver = (e: DragEvent): void => {
      if (disabled || !e.dataTransfer?.types.includes('Files')) return;
      e.preventDefault();
    };
    const onDragLeave = (): void => {
      depth = Math.max(0, depth - 1);
      if (depth === 0) setActive(false);
    };
    const onDrop = (e: DragEvent): void => {
      if (disabled || !e.dataTransfer?.types.includes('Files')) return;
      e.preventDefault();
      depth = 0;
      setActive(false);

      const items = Array.from(e.dataTransfer.items);
      const entries = items
        .map((i) => (i.webkitGetAsEntry?.() as unknown as FsEntry | null))
        .filter((x): x is FsEntry => x !== null);

      if (entries.length > 0) {
        void Promise.all(entries.map((entry) => walk(entry, '')))
          .then((groups) => { onFiles(groups.flat()); });
        return;
      }
      onFiles(Array.from(e.dataTransfer.files).map((file) => ({ file, relativePath: file.name })));
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [onFiles, disabled]);

  if (!active) return null;
  return (
    <div className="dropzone">
      <div className="dropzone__box row"><IconUpload size={20} /> 여기에 놓아 업로드</div>
    </div>
  );
}
